import { CssBaseline, ThemeProvider, useMediaQuery } from '@mui/material';
import * as React from 'react';
import { createMuiTheme } from 'theme/muiTheme';

export type ThemeMode = 'system' | 'dark' | 'light';

const STORAGE_KEY = 'themeMode';

export interface ThemeModeContextType {
  themeMode: ThemeMode;
  setThemeMode: (mode: ThemeMode) => void;
}

export const ThemeModeContext = React.createContext<ThemeModeContextType | undefined>(undefined);

const isValidThemeMode = (value: string | null): value is ThemeMode =>
  value === 'system' || value === 'dark' || value === 'light';

export const ThemeModeProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [themeMode, setThemeModeState] = React.useState<ThemeMode>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return isValidThemeMode(stored) ? stored : 'system';
  });

  const prefersDark = useMediaQuery('(prefers-color-scheme: dark)');
  const resolvedMode = themeMode === 'system' ? (prefersDark ? 'dark' : 'light') : themeMode;
  const theme = React.useMemo(() => createMuiTheme(resolvedMode), [resolvedMode]);

  const setThemeMode = React.useCallback((mode: ThemeMode) => {
    setThemeModeState(mode);
    localStorage.setItem(STORAGE_KEY, mode);
  }, []);

  React.useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key === STORAGE_KEY && isValidThemeMode(event.newValue)) {
        setThemeModeState(event.newValue);
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  return (
    <ThemeModeContext.Provider value={{ themeMode, setThemeMode }}>
      <ThemeProvider theme={theme}>
        <CssBaseline>{children}</CssBaseline>
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
};

export default ThemeModeProvider;
